// Customer-facing names for the `entity` of an audit change: a SQL entity (EF Core class name) or a Cosmos container
// key, as each audited service records it (Defatom.Shared docs/audit-trail-design.md §4.2–4.3).
//
// `primary` marks the record an action is about (a plan, a group, a policy); the other entities are rows written
// alongside it (a plan's devices and status history, its Cosmos detail document). The Target column leads with the
// first primary change, so "Create remediation plan" reads as the plan, not as whichever row happened to be saved first.

interface EntityInfo {
  label: string;
  primary: boolean;
}

const ENTITIES: Record<string, EntityInfo> = {
  // Remediation (PatchCenterAPI, AgentActionCreateJob, RemediationPlanStatus.Worker, TenantData.Worker)
  RemediationPlan: { label: 'Remediation plan', primary: true },
  TenantRemediationPlanDetail: { label: 'Remediation plan details', primary: false },
  RemediationPlanAgent: { label: 'Device in plan', primary: false },
  RemediationPlanKanbanBoard: { label: 'Plan status', primary: false },
  // Tenants, groups, devices, scripts, settings (TenantAPI, AgentAPI)
  Tenant: { label: 'Tenant', primary: true },
  TenantGroup: { label: 'Group', primary: true },
  TenantAgent: { label: 'Device', primary: true },
  TenantScriptStore: { label: 'Script', primary: true },
  TenantSystemSettings: { label: 'Tenant settings', primary: true },
  // Policies and deployments (GroupPolicyAutomationAPI, SoftwareDeploymentAPI, AssetManagementAPI)
  TenantGroupPolicyAutomation: { label: 'Group policy', primary: true },
  TenantSoftwareDeployment: { label: 'Software deployment', primary: true },
  TenantSoftwareComplianceList: { label: 'Software compliance policy', primary: true },
  // Global catalog (PatchRepositoryAPI)
  DefatomInstallerManifest: { label: 'Installer manifest', primary: true },
  PackageIdentifierToApplicationNameMapping: { label: 'Package mapping', primary: true },
  // Users, roles and app registrations (AuthService: ASP.NET Identity and IdentityServer stores)
  ApplicationUser: { label: 'User', primary: true },
  IdentityRole: { label: 'Role', primary: true },
  IdentityUserRole: { label: 'User role', primary: false },
  IdentityUserClaim: { label: 'User claim', primary: false },
  IdentityRoleClaim: { label: 'Role claim', primary: false },
  Client: { label: 'App registration', primary: true },
  ClientSecret: { label: 'App registration secret', primary: false },
  ClientScope: { label: 'App registration scope', primary: false },
  ClientClaim: { label: 'App registration claim', primary: false },
  ClientGrantType: { label: 'App registration grant type', primary: false },
  ClientRedirectUri: { label: 'App registration redirect URI', primary: false },
  ApiScope: { label: 'API scope', primary: true },
  ApiScopeClaim: { label: 'API scope claim', primary: false },
};

// Generic EF types are recorded with their arity, e.g. "IdentityUserRole`1".
const normalize = (entity: string) => entity.replace(/`\d+$/, '');

/** "TenantSoftwareThing" -> "Tenant software thing", for an entity not in the table (a service added later). */
const humanize = (entity: string) => {
  const words = entity
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return entity;
  return [words[0], ...words.slice(1).map((word) => (/^[A-Z0-9]+$/.test(word) ? word : word.toLowerCase()))].join(' ');
};

/** Customer-facing name of an entity; unknown entities are spelled out from their class/container name. */
export const entityLabel = (entity: string): string => ENTITIES[normalize(entity)]?.label ?? humanize(normalize(entity));

/** 0 = the record the action is about, 1 = an entity not in the table, 2 = a row written alongside it. */
export const entityRank = (entity: string): number => {
  const info = ENTITIES[normalize(entity)];
  if (!info) return 1;
  return info.primary ? 0 : 2;
};
